// Copied from LMV and modified for Tandem.

import {avp} from "@adsk/lmv";

const BVH = avp.BVH;

/** @import {FragmentStorage} from './DtVizLoad' */

/**
 * Compute BVH optimized for OutOfCoreTileManager.
 *
 * @param {RenderModel} model                  - The model to compute the BVH for
 * @returns {Promise<BVH>}                     - The computed BVH
 */
export async function computeOutOfCoreBVH(model) {
	const existingJob = avp.DefaultWorkerPool.findJob((job) => job.operation === "COMPUTE_BVH" && job.data.modelId === model.id);
	if (existingJob) {
		return (await existingJob.promise).bvhObject;
	}

	// We are copying the data from the fragment list into a format that can be passed to a worker.
	// This is necessary because the fragment list is not available in the worker.
	// This creates some temporary memory overhead though.
	const geomIds = model.loader.svf.fragments.geomDataIndexes;
	const materialIds = model.loader.svf.fragments.materials;

	/** @type {FragmentStorage} */
	const frags = model.getFragmentList().fragments;

	// We copy polygonCounts and flags, because they get transferred to the worker.
	// We could also just use the original ones and don't provide a transfer list, but it seems cleaner this way.
	const fragments = {
		boxes: frags.boxes,
		polygonCounts: frags.polygonCounts.slice(),
		flags: frags.fragmentFlags.slice(),
		materials: materialIds,
		length: frags.length,
		geomids: geomIds,
	};

	const transferList = [fragments.polygonCounts.buffer, fragments.flags.buffer];

	const bvhOptions = model.loader.loadContext.bvhOptions;

	const resultData = await avp.DefaultWorkerPool.enqueueJob("COMPUTE_BVH",
		{ fragments, modelId: model.id, bvhOptions }, transferList);

	const result = resultData.bvh;
	const bvh = new BVH(result.nodes.nodesRaw, result.primitives, bvhOptions, result.fragId2nodeId, result.nodeParents);

	// TODO-LMV: We're using the BOX_AREA heuristic to build the BVH (see DtLoader.loadFile).
	// This can cause BVH nodes that only contain lines (e.g. center lines) to report an area of 0
	// during rendering, which essentially skips them.
	// As a workaround, we set the size of the largest fragment in nodes with primitives, but zero size,
	// to a small value > 0.
	const nodes = bvh.nodes;
	for (let i = 0; i < nodes.nodeCount; ++i) {
		if (nodes.getPrimCount(i) && nodes.getLargestPrimitiveSize(i) === 0) {
			nodes.setLargestPrimitiveSize(i, 0.1);
		}
	}

	return bvh;
}
